import { PRIORITY_STATUS_LABEL, PRIORITY_STATUS_TOOLTIP } from "@/components/priorities/priorityCardTokens";
import { PriorityStatus, PriorityType } from "@/types";

/** Key order drives ASSIGNEE_STATUS_OPTIONS — Block Int / Block Ext stay adjacent, as in the filters. */
export const rollupStatusDisplay: Record<PriorityStatus, { label: string }> = {
    focus: { label: PRIORITY_STATUS_LABEL.focus },
    queue: { label: PRIORITY_STATUS_LABEL.queue },
    done: { label: PRIORITY_STATUS_LABEL.done },
    blocked: { label: PRIORITY_STATUS_LABEL.blocked },
    waiting: { label: PRIORITY_STATUS_LABEL.waiting },
    "on-hold": { label: PRIORITY_STATUS_LABEL["on-hold"] },
};

export const rollupStatusDot: Record<PriorityStatus, string> = {
    focus: "bg-blue-500",
    queue: "bg-slate-400",
    done: "bg-green-500",
    blocked: "bg-red-500",
    "on-hold": "bg-orange-400",
    waiting: "bg-yellow-400",
};

/**
 * `description` is the hover explanation for the block statuses. Select items can't host a Radix
 * tooltip, so callers pass it to the item's native `title`.
 */
export const ASSIGNEE_STATUS_OPTIONS = (Object.keys(rollupStatusDisplay) as PriorityStatus[]).map(
    (value) => ({
        value,
        label: rollupStatusDisplay[value].label,
        description: PRIORITY_STATUS_TOOLTIP[value],
    }),
);

/**
 * "Commit" is the only save that enforces the DRI + PPP gate. "Save as candidate" is the only one that
 * strips the DRI — candidacy is derived from having none, so the button has to actually send none.
 * "Save" is the plain save: whatever the form holds, gate-free.
 */
export type SubmitIntent = "candidate" | "save" | "commit";
export const COMMIT_GATE_MESSAGE = "Needs a DRI and a PPP before it can be committed.";

/** Per-user limits: one Focus, two Queue. Other statuses are unlimited escape valves. */
export const FOCUS_LIMIT = 1;
export const QUEUE_LIMIT = 2;

/** A user's Focus/Queue load across *other* priorities (excludes the one being edited). */
export interface AssigneeLoad {
    focus: number;
    queue: number;
}

export function isAssigneeStatusFull(status: PriorityStatus, load?: AssigneeLoad): boolean {
    if (!load) return false;
    if (status === "focus") return load.focus >= FOCUS_LIMIT;
    if (status === "queue") return load.queue >= QUEUE_LIMIT;
    return false;
}

/**
 * Whether a status option should be disabled for a user. Never disables the row's current value so
 * the form can't get stuck on stale data.
 */
export function isAssigneeStatusDisabled(
    status: PriorityStatus,
    load: AssigneeLoad | undefined,
    currentStatus: PriorityStatus,
): boolean {
    if (status === currentStatus) return false;
    return isAssigneeStatusFull(status, load);
}

/**
 * In create mode there's no per-assignee status picker — everyone defaults to Queue — so a user
 * already at the Queue limit can never be added validly. Block them in the picker upfront.
 */
export function isAssigneeAddDisabledAtCreate(load?: AssigneeLoad): boolean {
    return isAssigneeStatusFull("queue", load);
}

export const avatarDriClass =
    "h-5 w-5 shrink-0 border border-primary bg-primary/5 ring-1 ring-primary/10";
export const avatarDriFallbackClass = "text-[10px] font-semibold bg-primary/5 text-primary";

export const avatarAssigneeClass =
    "h-5 w-5 shrink-0 border-2 border-background ring-1 ring-border";
export const avatarAssigneeFallbackClass = "text-[10px] font-medium bg-muted";

export const PRIORITY_TYPE_LABEL: Record<PriorityType, string> = {
    SHP: "SHP",
    WKR: "WKR",
    DAS: "DAS",
};

export const fieldLabelClass =
    "mb-1.5 block text-xs font-medium tracking-normal text-muted-foreground";
export const sectionKickerClass =
    "mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground";

export const cardClass =
    "overflow-hidden rounded-xl border border-border/80 bg-background shadow-sm";
export const cardHeaderClass =
    "flex items-center justify-between border-b border-border bg-muted/20 px-5 py-3";
export const cardBodyClass = "p-5";

export function formatReportDate(value: string) {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "Unknown date";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export interface DisplayUser {
    pid: string;
    fullName: string;
    avatarUrl?: string;
    position?: string;
}
